import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { cleanTagName, defaultTagColor, TAG_COLORS, type Tag } from '@tally/shared';
import { Check, Pencil, Plus, Repeat, Sparkles, Trash2, X } from 'lucide-react';
import { tagsApi } from '@/services/api';
import { ROUTES } from '@/routes';
import { cn } from '@/utils/cn';
import { formatUsd } from '@/utils/money';
import { invalidateExpenseQueries, useTags } from '@/components/tags/tagQueries';
import { FindMatchesDialog } from '@/components/tags/FindMatchesDialog';

type Kind = 'all' | Tag['kind'];

export function TagsPage() {
  const queryClient = useQueryClient();
  const tags = useTags();
  const [kind, setKind] = useState<Kind>('all');
  const [name, setName] = useState('');
  const [matching, setMatching] = useState<{ id: string; name: string } | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: (n: string) => tagsApi.create({ name: n }),
    onSuccess: (res, n) => {
      queryClient.invalidateQueries({ queryKey: ['tags'] });
      setName('');
      // A new tag starts empty: offer the AI's picks right away.
      setMatching({ id: res.data.id, name: n });
    },
  });
  const runAi = useMutation({
    mutationFn: () => tagsApi.runAi(),
    onSuccess: (res) => {
      invalidateExpenseQueries(queryClient);
      const d = res.data;
      setMessage(`AI looked at ${d.asked} new merchant${d.asked === 1 ? '' : 's'}; ${d.tagsApplied} tag${d.tagsApplied === 1 ? '' : 's'} applied${d.pending ? `, ${d.pending} merchants left for the next run` : ''}.`);
    },
    onError: (err) => setMessage(err instanceof Error ? err.message : 'AI tagging failed'),
  });

  const shown = tags.filter((t) => kind === 'all' || t.kind === kind).sort((a, b) => b.totalUsd - a.totalUsd);
  const newName = cleanTagName(name);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Tags</h1>
          <p className="text-sm text-muted-foreground">Group expenses any way you like. Trips are tags too; subscriptions and AI add their own.</p>
        </div>
        <button
          onClick={() => runAi.mutate()}
          disabled={runAi.isPending}
          className="flex items-center gap-2 rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-accent disabled:opacity-50"
        >
          <Sparkles className={cn('h-4 w-4 text-primary', runAi.isPending && 'animate-pulse')} /> {runAi.isPending ? 'Tagging…' : 'Tag with AI now'}
        </button>
      </div>
      {message && <p className="rounded-lg border border-border bg-muted/30 px-4 py-2 text-sm" role="status">{message}</p>}

      <form
        onSubmit={(e) => { e.preventDefault(); if (newName) create.mutate(newName); }}
        className="flex flex-wrap items-center gap-2"
      >
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="New tag, e.g. AI Tools or Kids"
          aria-label="New tag name"
          className="min-w-[220px] flex-1 rounded-lg border border-input bg-background px-3 py-2 text-sm sm:max-w-sm"
        />
        <button type="submit" disabled={!newName || create.isPending} className="flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
          <Plus className="h-4 w-4" /> Create
        </button>
        {create.error && <p className="text-xs text-destructive">{create.error instanceof Error ? create.error.message : 'Could not create'}</p>}
      </form>

      <div className="flex w-fit items-center gap-1 rounded-lg border border-border p-1" role="tablist">
        {(['all', 'topic', 'recurring'] as Kind[]).map((k) => (
          <button key={k} role="tab" aria-selected={kind === k} onClick={() => setKind(k)} className={cn('rounded-md px-3 py-1.5 text-sm font-medium', kind === k ? 'bg-accent text-foreground' : 'text-muted-foreground hover:text-foreground')}>
            {k === 'all' ? 'All' : k === 'topic' ? 'Topics' : 'Subscriptions'}
          </button>
        ))}
      </div>

      {shown.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
          No tags yet. Create one above, or tag expenses from the Transactions page.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {shown.map((t) => <TagCard key={t.id} tag={t} onFind={() => setMatching({ id: t.id, name: t.name })} />)}
        </div>
      )}

      {matching && <FindMatchesDialog tag={matching} onClose={() => setMatching(null)} />}
    </div>
  );
}

function TagCard({ tag, onFind }: { tag: Tag; onFind: () => void }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(tag.name);
  const [confirming, setConfirming] = useState(false);
  const color = tag.color ?? defaultTagColor(tag.name);

  const update = useMutation({
    mutationFn: (body: { name?: string; color?: string }) => tagsApi.update(tag.id, body),
    onSuccess: () => { invalidateExpenseQueries(queryClient); setEditing(false); },
  });
  const remove = useMutation({ mutationFn: () => tagsApi.remove(tag.id), onSuccess: () => invalidateExpenseQueries(queryClient) });
  const nextColor = TAG_COLORS[(TAG_COLORS.indexOf(color) + 1) % TAG_COLORS.length];

  return (
    <div className="flex flex-col rounded-xl border border-border bg-card p-5">
      <div className="flex items-center gap-2">
        <button
          onClick={() => update.mutate({ color: nextColor })}
          className="h-4 w-4 shrink-0 rounded-full ring-2 ring-transparent hover:ring-border"
          style={{ backgroundColor: color }}
          aria-label="Change color"
          title="Change color"
        />
        {editing ? (
          <form className="flex flex-1 items-center gap-1" onSubmit={(e) => { e.preventDefault(); const n = cleanTagName(draft); if (n) update.mutate({ name: n }); }}>
            <input autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} aria-label="Tag name" className="min-w-0 flex-1 rounded-md border border-input bg-background px-2 py-1 text-sm" />
            <button type="submit" className="rounded p-1 hover:bg-accent" aria-label="Save"><Check className="h-4 w-4" /></button>
            <button type="button" onClick={() => { setEditing(false); setDraft(tag.name); }} className="rounded p-1 hover:bg-accent" aria-label="Cancel"><X className="h-4 w-4" /></button>
          </form>
        ) : (
          <>
            <p className="min-w-0 flex-1 truncate font-semibold" title={tag.name}>{tag.name}</p>
            <button onClick={() => setEditing(true)} className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground" aria-label={`Rename ${tag.name}`}><Pencil className="h-3.5 w-3.5" /></button>
          </>
        )}
        {tag.kind === 'recurring' && <span className="flex shrink-0 items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground"><Repeat className="h-3 w-3" /> recurring</span>}
      </div>
      {update.error && <p className="mt-1 text-xs text-destructive">{update.error instanceof Error ? update.error.message : 'Could not save'}</p>}

      <p className="mt-3 text-2xl font-bold">{formatUsd(tag.totalUsd)}</p>
      <p className="text-xs text-muted-foreground">{tag.transactionCount} expense{tag.transactionCount === 1 ? '' : 's'}</p>

      <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-border pt-3 text-xs">
        <Link to={`${ROUTES.app.transactions}?tagId=${encodeURIComponent(tag.id)}`} className="font-medium text-primary hover:underline">View expenses</Link>
        <button onClick={onFind} className="flex items-center gap-1 font-medium text-primary hover:underline"><Sparkles className="h-3 w-3" /> Find more</button>
        <span className="flex-1" />
        {confirming ? (
          <span className="flex items-center gap-2">
            <span className="text-destructive">Delete?</span>
            <button onClick={() => remove.mutate()} disabled={remove.isPending} className="font-medium text-destructive hover:underline">Yes</button>
            <button onClick={() => setConfirming(false)} className="text-muted-foreground hover:underline">No</button>
          </span>
        ) : (
          <button onClick={() => setConfirming(true)} className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-destructive" aria-label={`Delete ${tag.name}`}><Trash2 className="h-3.5 w-3.5" /></button>
        )}
      </div>
    </div>
  );
}
